class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
  }
}
customElements.define('modal-dialog', ModalDialog);
if (!customElements.get('newsletter-popup-controller')) {
  class NewsletterPopupController extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.modal = document.querySelector(this.dataset.modalSelector);
      this.focusAnchor = this.querySelector('.newsletter-popup__focus-anchor');

      if (!this.modal) return;

      this.storageKey = this.dataset.storageKey;
      this.autoOpen = this.dataset.autoOpen === 'true';
      this.delayMs = Number(this.dataset.delayMs) || 0;
      this.showAgainDays = Number(this.dataset.showAgainDays) || 0;
      this.forceOpen = Boolean(this.modal.querySelector('[data-force-open="true"]'));

      this.handleModalClick = this.handleModalClick.bind(this);
      this.handleModalKeyup = this.handleModalKeyup.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);
      this.container = this;

      this.modal.addEventListener('click', this.handleModalClick);
      this.modal.addEventListener('keyup', this.handleModalKeyup);
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);

      this.submitForm();
      // const form = this.modal.querySelector('form');
      // if (form) {
      //   form.addEventListener('submit', () => this.persistDismissal());
      // }

      if (this.modal.querySelector('[data-success="true"]')) {
        this.persistDismissal();
      }

      if (window.Shopify && Shopify.designMode && !this.forceOpen) return;
      if (!this.forceOpen && (!this.autoOpen || this.dismissedRecently())) return;

      this.openTimer = window.setTimeout(() => {
        if (!this.modal.hasAttribute('open')) this.modal.show(this.focusAnchor);
      }, this.forceOpen ? 0 : this.delayMs);
    }

    submitForm() {
      const section = document.querySelector(`.newsletter-popup-modal__body`);
      const form = section.querySelector(".newsletter-signup__form");
      const submitButton = section.querySelector(".newsletter-signup__submit");
      const successMessage = section.querySelector(".newsletter-signup__message--success");
      const errorMessage = section.querySelector(".newsletter-signup__message--error");
      const hasFeedback = section.querySelector(
        ".newsletter-signup__message--success:not([hidden]), .newsletter-signup__message--error:not([hidden])"
      );
      const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hideMessages = () => {
        if (successMessage) {
          successMessage.hidden = true;
        }
        if (errorMessage) {
          errorMessage.hidden = true;
          errorMessage.textContent = "";
        }
      };

      const showError = (message) => {
        if (!errorMessage) return;
        errorMessage.textContent = message;
        errorMessage.hidden = false;
      };

      const setLoading = (loading) => {
        if (!submitButton) return;
        submitButton.disabled = loading;
        submitButton.classList.toggle("is-loading", loading);
        section.classList.toggle("is-submitting", loading);
      };

      const getErrorMessage = async (response, fallback) => {
        try {
          const payload = await response.json();
          const detail = payload?.errors?.[0]?.detail;
          return detail || fallback;
        } catch (error) {
          return fallback;
        }
      };
      form?.addEventListener("submit", async (event) => {
        event.preventDefault();
        hideMessages();

        const companyId = (this.container.dataset.klaviyoCompanyId || "").trim();
        const listId = (this.container.dataset.klaviyoListId || "").trim();
        const revision = (this.container.dataset.klaviyoRevision || "2026-04-15").trim();
        const invalidEmailMessage =
          this.container.dataset.errorInvalidEmail || "Please enter a valid email address.";
        const submitErrorMessage =
          this.container.dataset.errorSubmit || "Something went wrong. Please try again.";

        if (!companyId || !listId) {
          showError("Klaviyo is not configured. Please set company ID and list ID in theme settings.");
          return;
        }

        const formData = new FormData(form);
        const email = (formData.get("email") || "").toString().trim();
        const firstName = (formData.get("first_name") || "").toString().trim();
        const lastName = (formData.get("last_name") || "").toString().trim();

        if (!EMAIL_REGEX.test(email)) {
          showError(invalidEmailMessage);
          return;
        }

        setLoading(true);

        try {
          const response = await fetch(
            `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
            {
              method: "POST",
              headers: {
                revision,
                "content-type": "application/json",
              },
              body: JSON.stringify({
                data: {
                  type: "subscription",
                  attributes: {
                    profile: {
                      data: {
                        type: "profile",
                        attributes: {
                          email,
                        },
                      },
                    },
                  },
                  relationships: {
                    list: {
                      data: {
                        type: "list",
                        id: listId,
                      },
                    },
                  },
                },
              }),
            }
          );

          if (response.status === 202) {
            form.reset();
            if (successMessage) {
              successMessage.hidden = false;
            }
            return;
          }

          const apiMessage = await getErrorMessage(response, submitErrorMessage);
          showError(apiMessage);
        } catch (error) {
          showError(submitErrorMessage);
        } finally {
          setLoading(false);
          this.persistDismissal();
        }
      });
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      if (!this.modal) return;

      this.modal.removeEventListener('click', this.handleModalClick);
      this.modal.removeEventListener('keyup', this.handleModalKeyup);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
    }

    dismissedRecently() {
      if (!this.showAgainDays) return false;

      try {
        const dismissedAt = Number(window.localStorage.getItem(this.storageKey));
        if (!dismissedAt) return false;
        return Date.now() - dismissedAt < this.showAgainDays * 24 * 60 * 60 * 1000;
      } catch (error) {
        return false;
      }
    }

    persistDismissal() {
      if (!this.showAgainDays) return;

      try {
        window.localStorage.setItem(this.storageKey, String(Date.now()));
      } catch (error) {
        // Ignore storage failures so the popup still works in restricted browsers.
      }
    }

    handleModalClick(event) {
      if (event.target === this.modal || event.target.closest('[id^="ModalClose-"]')) {
        this.persistDismissal();
      }
    }

    handleModalKeyup(event) {
      if (event.code.toUpperCase() === 'ESCAPE') {
        this.persistDismissal();
      }
    }

    handleSectionSelect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;
      if (!this.modal.hasAttribute('open')) this.modal.show(this.focusAnchor);
    }

    handleSectionDeselect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;
      if (this.modal.hasAttribute('open')) this.modal.hide();
    }
  }

  customElements.define('newsletter-popup-controller', NewsletterPopupController);
}